import React, { useState, useMemo } from 'react'
//@ts-ignore
import { Badge } from 'dsm-react-lib'
import {
  IconSearch,
  IconCode,
  IconFileText,
  IconList,
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconTrash,
  IconNavigation,
  IconLayoutSidebar,
  IconClipboard
} from '@tabler/icons-react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import {
  formatJsonForMarkdown,
  linkifyPlainUrls,
  parseWebSearchResults,
  WebSearchResultItem
} from './utils'
import { fetchDomain } from '../utils/helpers'
import { useThemeManager } from '../../../../_hooks/useThemeManager'

interface ReasoningToolCallProps {
  type:
    | 'grep'
    | 'search'
    | 'code'
    | 'edit'
    | 'delete'
    | 'todo'
    | 'read'
    | 'navigate'
    | 'panel'
    | 'clipboard'
  action?: string
  context: string
  details?: string | Record<string, unknown>
  result?: any // Can be string, object, or any JSON-serializable type
  status: 'completed' | 'running' | 'pending'
  isFrontend?: boolean
  showRunningBadge?: boolean
}

export const ToolCallCard: React.FC<ReasoningToolCallProps> = ({
  type,
  action,
  context,
  details,
  result,
  status,
  isFrontend = false,
  showRunningBadge = status === 'running'
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { isDarkTheme: isDark } = useThemeManager()

  const handleToggle = () => {
    setIsExpanded((expanded) => !expanded)
  }

  const getContentItems = (resultInput: any): Array<{ type: string; text: string }> => {
    if (resultInput === null || resultInput === undefined) return []

    let parsed: any = resultInput

    if (typeof resultInput === 'string') {
      try {
        parsed = JSON.parse(resultInput)
      } catch (e) {
        return [{ type: 'text', text: resultInput }]
      }
    }

    if (parsed && Array.isArray(parsed.content)) {
      return parsed.content
        .filter((c: any) => c && c.type === 'text' && c.text)
        .map((c: any) => ({ type: c.type, text: c.text }))
    }

    if (Array.isArray(parsed) && parsed.every((p) => typeof p === 'string')) {
      return parsed.map((t: string) => ({ type: 'text', text: t }))
    }

    if (parsed && typeof parsed === 'object' && typeof parsed.text === 'string') {
      return [{ type: 'text', text: parsed.text }]
    }

    return []
  }

  const formatTextAsHtml = (text: string): string => {
    if (!text) return ''

    const source = formatJsonForMarkdown(text)

    try {
      const rawHtml = marked.parse(source, {
        breaks: true,
        gfm: true,
        silent: true
      })

      const sanitized = DOMPurify.sanitize(rawHtml as string, {
        ADD_ATTR: ['target', 'class', 'style']
      })

      const styledHtml = linkifyPlainUrls(sanitized)
        .replace(/<p>/g, '<p style="margin: 0.5em 0;">')
        .replace(/<ul>/g, '<ul style="margin: 0.5em 0; padding-left: 1.5em;">')
        .replace(/<ol>/g, '<ol style="margin: 0.5em 0; padding-left: 1.5em;">')
        .replace(/<li>/g, '<li style="margin: 0.25em 0;">')
        .replace(
          /<code>/g,
          `<code style="background: ${
            isDark ? 'var(--surface-bg)' : 'var(--gray-100)'
          }; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: monospace; font-size: 0.9em;">`
        )
        .replace(
          /<pre>/g,
          `<pre style="background: ${isDark ? 'var(--surface-bg)' : 'var(--gray-100)'}; color: ${
            isDark ? 'var(--text-body)' : 'var(--text-item)'
          }; padding: 0.75rem; border-radius: 0.375rem; max-width: 100%; margin: 0.5em 0; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.8125rem; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">`
        )
        .replace(
          /<code class="language-[^"]*">/g,
          '<code style="background: transparent; padding: 0; border-radius: 0; color: inherit; font-family: inherit; font-size: inherit; white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word;">'
        )
        .replace(
          /<h1>/g,
          '<h1 style="font-size: 1.25em; font-weight: 600; margin: 0.75em 0 0.5em;">'
        )
        .replace(
          /<h2>/g,
          '<h2 style="font-size: 1.125em; font-weight: 600; margin: 0.75em 0 0.5em;">'
        )
        .replace(/<h3>/g, '<h3 style="font-size: 1em; font-weight: 600; margin: 0.75em 0 0.5em;">')
        .replace(
          /<blockquote>/g,
          `<blockquote style="border-left: 3px solid ${
            isDark ? 'var(--border-hover)' : 'var(--gray-200)'
          }; padding-left: 1em; margin: 0.5em 0; color: ${
            isDark ? 'var(--text-subheading)' : 'var(--gray-500)'
          };">`
        )
        .replace(/<a /g, `<a style="color: var(--color-primary); text-decoration: underline; " `)

      return styledHtml
    } catch (error) {
      console.error('Markdown formatting error:', error)
      return `<pre style="white-space: pre-wrap; word-break: break-word; font-family: inherit;">${text
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</pre>`
    }
  }

  const contentItems = useMemo(() => getContentItems(result), [result])
  const hasOutput = contentItems.length > 0
  const input = useMemo(() => {
    const value =
      typeof details === 'string' ? details.trim() : details ? JSON.stringify(details) : ''
    if (!value) return { text: '', isReady: false }

    try {
      return { text: JSON.stringify(JSON.parse(value), null, 2), isReady: true }
    } catch {
      const isIncompleteJson = value.startsWith('{') || value.startsWith('[')
      return { text: value, isReady: !isIncompleteJson }
    }
  }, [details])
  const hasInput = input.isReady
  const hasContent = hasInput || hasOutput
  const [resultId] = useState(() => `toolcall-result-${Math.random().toString(36).slice(2, 9)}`)

  const renderSourceList = (results: WebSearchResultItem[]) => (
    <ul className="flex flex-col gap-2 list-none p-0 m-0">
      {results.slice(0, 6).map((source, sourceIndex) => {
        const domain = fetchDomain(source.link)
        return (
          <li key={sourceIndex} className="flex items-start gap-1">
            <a
              href={source.link}
              target="_blank"
              rel="noopener noreferrer"
              title={source.title}
              className="text-sm font-medium leading-[1.4] truncate min-w-0 hover:underline"
              style={{ color: 'var(--color-primary)' }}>
              {source.title}
            </a>
            {domain && (
              <span
                className={`caption-text flex-shrink-0 ${
                  isDark ? 'text-[color:var(--text-disabled)]' : 'text-[color:var(--gray-400)]'
                }`}>
                {domain}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )

  const getIcon = () => {
    switch (type) {
      case 'grep':
      case 'search':
        return <IconSearch size={16} />
      case 'code':
        return <IconCode size={16} />
      case 'edit':
        return <IconEdit size={16} stroke={1.5} />
      case 'delete':
        return <IconTrash size={16} stroke={1.5} />
      case 'todo':
        return <IconList size={16} />
      case 'read':
        return <IconFileText size={16} />
      case 'navigate':
        return <IconNavigation size={16} />
      case 'panel':
        return <IconLayoutSidebar size={16} />
      case 'clipboard':
        return <IconClipboard size={16} />
      default:
        return <IconFileText size={16} />
    }
  }

  const getActionLabel = () => {
    switch (type) {
      case 'grep':
        return 'Grep'
      case 'search':
        return 'Search'
      case 'code':
        return 'Code Interpreter'
      case 'edit':
        return 'Edit'
      case 'delete':
        return 'Delete'
      case 'todo':
        return 'Todo'
      case 'read':
        return 'Read'
      case 'navigate':
        return 'Navigate'
      case 'panel':
        return 'Panel'
      case 'clipboard':
        return 'Clipboard'
      default:
        return type
    }
  }

  const getContainerClasses = () => {
    let classes = 'flex flex-col p-1 rounded-lg transition-colors duration-200'

    if (isDark) {
      classes +=
        ' bg-[color:var(--surface-muted)] border border-[color:var(--theme-border)] hover:border-[color:var(--border-hover)] focus-within:border-[color:var(--border-focus)]'
    } else {
      classes +=
        ' bg-surface border border-[color:var(--surface-muted)] hover:border-[color:var(--theme-border)] focus-within:border-[color:var(--border-focus)]'
    }

    return classes
  }

  const getIconClasses = () => {
    let classes = 'flex items-center justify-center w-5 h-5 flex-shrink-0'

    if (status === 'running') {
      classes += ' text-[color:var(--color-success)] animate-thinking-pulse'
    } else if (status === 'completed') {
      classes += ' text-[color:var(--color-success)]'
    } else {
      classes += isDark ? ' text-white' : ' text-[color:var(--text-item)]'
    }

    return classes
  }

  const toolLabel = action ?? getActionLabel()

  return (
    <div
      className={`w-full min-w-0 box-border overflow-hidden ${getContainerClasses()}`}
      data-testid={`thinking-toolcall-${status}`}>
      <button
        type="button"
        className={`flex items-start gap-1 w-full text-left bg-transparent border-0 p-0 ${
          hasContent ? 'cursor-pointer' : 'cursor-default'
        }`}
        onClick={hasContent ? handleToggle : undefined}
        disabled={!hasContent}
        aria-label={hasContent ? (isExpanded ? 'Collapse details' : 'Expand details') : undefined}
        aria-expanded={hasContent ? isExpanded : undefined}
        aria-controls={hasContent ? resultId : undefined}>
        <div className={`${getIconClasses()} mt-0.5`} data-testid="thinking-toolcall-icon">
          {getIcon()}
        </div>
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <span
            className={`font-semibold text-sm leading-[1.571em] flex-shrink-0 ${
              isDark ? 'text-white' : 'text-[color:var(--text-item)]'
            }`}>
            {toolLabel}
          </span>
          {showRunningBadge && <Badge color="info" label="Running" />}
          {isFrontend && (
            <span
              className="inline-flex items-center px-1 py-0 text-xs font-medium rounded bg-blue-100 text-blue-800 flex-shrink-0"
              data-testid="frontend-badge">
              Frontend
            </span>
          )}
          <span
            title={context}
            className={`text-sm leading-[1.571em] truncate min-w-0 ${
              isDark ? 'text-[color:var(--text-subheading)]' : 'text-subheading'
            }`}>
            {context}
          </span>
        </div>
        {hasContent && (
          <span
            aria-hidden="true"
            className={`flex items-center justify-center w-5 h-5 mt-0.5 bg-transparent border-none cursor-pointer p-0 transition-colors duration-200 flex-shrink-0 ${
              isDark
                ? 'text-white hover:text-[color:var(--text-subheading)]'
                : 'text-[color:var(--text-item)] hover:text-dark'
            }`}>
            {isExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
          </span>
        )}
      </button>

      {/* Inline result rendered directly below label when expanded. No "Result" header or extra box. */}
      {isExpanded && hasContent && (
        <div
          id={resultId}
          role="region"
          aria-hidden={!isExpanded}
          className="flex flex-col gap-1 mt-1 w-full pr-1">
          {hasInput && (
            <section
              className="rounded-md border border-[color:var(--theme-border)] overflow-hidden"
              data-testid="toolcall-input">
              <div className="px-2 py-1 text-xs font-semibold text-[color:var(--text-item)] bg-[color:var(--surface-muted)] border-b border-[color:var(--theme-border)]">
                Input
              </div>
              <pre className="m-0 p-2 min-h-24 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[13px] leading-[1.5] text-[color:var(--text-body)] bg-[color:var(--surface-bg)]">
                {input.text}
              </pre>
            </section>
          )}
          {hasOutput && (
            <section
              className="rounded-md border border-[color:var(--theme-border)] overflow-hidden"
              data-testid="toolcall-output">
              <div className="px-2 py-1 text-xs font-semibold text-[color:var(--text-item)] bg-[color:var(--surface-muted)] border-b border-[color:var(--theme-border)]">
                Output
              </div>
              <div className="p-2 min-h-48 max-h-96 overflow-auto bg-[color:var(--surface-bg)]">
                {contentItems.map((item, index) => {
                  const sources = parseWebSearchResults(item.text)
                  return (
                    <div
                      key={index}
                      className={
                        index > 0 ? 'pt-1 border-t border-[color:var(--theme-border)]' : undefined
                      }>
                      {sources ? (
                        renderSourceList(sources)
                      ) : (
                        <div
                          className={`caption-text leading-[1.6] markdown-result break-words max-w-full ${
                            isDark
                              ? 'text-[color:var(--text-body)]'
                              : 'text-[color:var(--text-item)]'
                          }`}
                          dangerouslySetInnerHTML={{ __html: formatTextAsHtml(item.text) }}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
